import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Page from "../../common/page/Page"
import axios from "axios"
import ResponsiveTable from "../../common/ResponsiveTable/ResponsiveTable"
import Loader from "../../common/loader/Loader"
import { useFlashMessage } from "../../common/hook/useFlashMessage"
import FlashMessage from "../../common/FlashMesage/FlashMessage"
import DoubleArrowIcon from "../../icons/DoubleArrowIcon"
import { useAuth } from "../../data/useAuth"
import C_AddressSlip from "./C_AddressSlip/C_AddressSlip"
import { decodeHtml } from "../../common/utils/address.util"
import styles from "./AdminOrdersPage.module.css"

const AdminOrdersPage = () => {
  const { flash, showMessage, hideMessage } = useFlashMessage()
  const navigate = useNavigate()
  const [allOrders, setAllOrders] = useState([])
  const [orders, setOrders] = useState([])
  const [statusFilter, setStatusFilter] = useState("")
  const [statusList, setStatusList] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { signOut } = useAuth()
  const [addressSlipClicked, setAddressSlipClicked] = useState(false)
  const [dateRange, setDateRange] = useState({
    from: "",
    to: "",
  })

  const getStatusBadgeClass = (status) => {
    if (!status) return styles.statusDefault
    const lower = status.toLowerCase()
    if (
      lower.includes("verif") ||
      lower.includes("deliver") ||
      lower.includes("success")
    ) {
      return styles.statusVerified
    }
    if (lower.includes("pend") || lower.includes("process")) {
      return styles.statusPending
    }
    if (lower.includes("fail") || lower.includes("cancel")) {
      return styles.statusFailed
    }
    return styles.statusDefault
  }

  const downloadCSV = () => {
    if (orders.length === 0) {
      showMessage("No orders available to download", "warning")
      return
    }

    const header = [
      "Order ID",
      "Customer",
      "Total ₹",
      "Products x Qty",
      "Status",
      "Date",
      "Address",
    ]

    const rows = orders.map((order) => {
      const address = `${decodeHtml(order.name)}
${decodeHtml(order.line_1)}
${decodeHtml(order.line_2)}
${decodeHtml(order.city)}, ${decodeHtml(order.district)}
${decodeHtml(order.state)}, ${decodeHtml(order.country)} - ${order.pin_code}
${order.phone_number}`

      const products = `"${order.order_products
        .map((product) => `${decodeHtml(product.Product?.name || "")} x ${product.qty}`)
        .join("\n")}"`
      // Escape double quotes in address if any
      const safeAddress = `"${address.replace(/"/g, '""')}"`
      const safeCustomer = `"${decodeHtml(order.name).replace(/"/g, '""')}"`
      return [
        order.id,
        safeCustomer,
        (
          parseFloat(order.amount || 0) +
          parseFloat(order.payment_gateway_charge || 0) +
          parseFloat(order.delivery_charge || 0) +
          parseFloat(order.gst || 0)
        ).toFixed(2),
        products,
        order.order_statuses?.[0]?.status || "N/A",
        new Date(order.created_at).toLocaleDateString(),
        safeAddress,
      ]
    })

    const csvContent = [header, ...rows].map((e) => e.join(",")).join("\n")

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.setAttribute("href", url)
    link.setAttribute(
      "download",
      `orders_${dateRange.from}_to_${dateRange.to}.csv`
    )
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleStatusChange = (value) => {
    setStatusFilter(value)

    if (value === "") {
      setOrders(allOrders)
    } else {
      const filtered = allOrders.filter(
        (order) => order.order_statuses?.[0]?.status === value
      )
      setOrders(filtered)
    }
  }

  useEffect(() => {
    const istOffset = 5.5 * 60 * 60 * 1000
    const day = new Date()
    const today = new Date(day.getTime() + istOffset)
    const yesterday = new Date(day.getTime() + istOffset)
    yesterday.setDate(today.getDate() - 1)
    const formatDate = (date) => date.toISOString().split("T")[0]

    setDateRange({
      from: formatDate(yesterday),
      to: formatDate(today),
    })
  }, [])

  useEffect(() => {
    if (dateRange.from && dateRange.to) fetchOrders()
  }, [dateRange])

  useEffect(() => {
    handleStatusChange(statusFilter)
  }, [allOrders])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const response = await axios.get(
        `/api/orders/date-range?from=${dateRange.from}&to=${dateRange.to}`
      )
      setAllOrders(response.data || [])
      setOrders(response.data || [])
      setStatusList(
        new Set(
          response.data
            ?.map((x) => x.order_statuses)
            .map((y) => y.map((z) => z.status)[0])
        )
      )
    } catch (err) {
      signOut()
      setError(err.response?.data?.error || err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { id, value } = e.target
    setDateRange((prev) => ({ ...prev, [id]: value }))
  }

  return (
    <Page title={"Orders"} is_admin={true}>
      <div className={styles.container}>
        <div className={styles.filterCard}>
          <form
            className={styles.filterForm}
            onSubmit={(e) => e.preventDefault()}
          >
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel} htmlFor="status">
                Status
              </label>
              <select
                id="status"
                className={styles.filterSelect}
                value={statusFilter}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="">All Statuses</option>
                {statusList &&
                  [...statusList].filter(Boolean).map((x, k) => (
                    <option value={x} key={k}>
                      {x}
                    </option>
                  ))}
              </select>
            </div>

            <div className={styles.filterRowDates}>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel} htmlFor="from">
                  From Date
                </label>
                <input
                  type="date"
                  id="from"
                  className={styles.filterInput}
                  value={dateRange.from}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel} htmlFor="to">
                  To Date
                </label>
                <input
                  type="date"
                  id="to"
                  className={styles.filterInput}
                  value={dateRange.to}
                  onChange={handleInputChange}
                  required
                />
              </div>
            </div>
          </form>
        </div>

        {loading && (
          <div className={styles.loadingContainer}>
            <Loader />
          </div>
        )}

        {error && !loading && (
          <div className={styles.errorState}>
            ⚠️ Failed to load orders: {error}
          </div>
        )}

        {!loading && !error && !addressSlipClicked && (
          orders.length === 0 ? (
            <div className={styles.emptyState}>
              <p style={{ margin: 0, fontWeight: 500 }}>
                No orders found for the selected date range and status.
              </p>
            </div>
          ) : (
            <ResponsiveTable>
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Total ₹</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => {
                  const statusText = order.order_statuses?.[0]?.status || "N/A"
                  return (
                    <tr key={order.id}>
                      <td data-label="Order ID">
                        <span style={{ fontWeight: 600 }}>#{order.id}</span>
                      </td>
                      <td data-label="Customer">
                        <span>{decodeHtml(order.name)}</span>
                      </td>
                      <td data-label="Total">
                        <span style={{ fontWeight: 600 }}>
                          ₹{" "}
                          {(
                            parseFloat(order.amount || 0) +
                            parseFloat(order.payment_gateway_charge || 0) +
                            parseFloat(order.delivery_charge || 0) +
                            parseFloat(order.gst || 0)
                          ).toFixed(2)}
                        </span>
                      </td>
                      <td data-label="Status">
                        <span
                          className={`${styles.statusBadge} ${getStatusBadgeClass(
                            statusText
                          )}`}
                        >
                          {statusText}
                        </span>
                      </td>
                      <td data-label="Date">
                        <span>
                          {new Date(order.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td data-label="Actions">
                        <span>
                          <button
                            type="button"
                            onClick={() => navigate(`/orders/${order.id}`)}
                            className={styles.actionIconBtn}
                            aria-label={`View details for order #${order.id}`}
                            title={`View Order #${order.id}`}
                          >
                            <DoubleArrowIcon />
                          </button>
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </ResponsiveTable>
          )
        )}

        {!addressSlipClicked && !loading && (
          <div className={styles.actionBar}>
            <button
              type="button"
              onClick={downloadCSV}
              className={styles.csvBtn}
              disabled={orders.length === 0}
            >
              📥 Download CSV
            </button>
            <button
              type="button"
              onClick={() => {
                if (orders.length === 0) {
                  showMessage("No orders available to generate address slips", "warning")
                  return
                }
                setAddressSlipClicked(true)
              }}
              className={styles.slipBtn}
              disabled={orders.length === 0}
            >
              📄 View Address Slip
            </button>
          </div>
        )}

        {addressSlipClicked && (
          <C_AddressSlip
            orders={orders}
            setAddressSlipClicked={setAddressSlipClicked}
          />
        )}
      </div>

      {flash && (
        <FlashMessage
          message={flash.message}
          type={flash.type}
          mode={flash.mode}
          onClose={hideMessage}
        />
      )}
    </Page>
  )
}

export default AdminOrdersPage
